import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, EventEmitter, ViewChild, ViewEncapsulation, Input, Output, HostListener } from '@angular/core';
import { formatNumber } from '@angular/common';
import { concat, of, from, filter, Subscription, Subject } from 'rxjs';
import { tap, map, switchMap, concatMap, finalize } from 'rxjs/operators';
import { Router} from '@angular/router';

import { MessageService, FilterService } from 'primeng/api';
import { Table } from 'primeng/table';
import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { Product, ProductParts, ProductInfo } from 'src/app/shared/product'

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { ProductApiService } from 'src/app/services/product-api.service';
import { DocumentApiService } from 'src/app/services/document-api.service';

/**
 * Editable bill-of-materials (BOM) table for a product's component parts.
 *
 * On init and on `product` input change, `loadProdParts` fetches `ProductParts[]` via
 * `getProdParts`, then enriches each row with live pricing and stock data from
 * `getProdInfoById` using `concatMap` to avoid parallel requests.  Totals are
 * recalculated by `countIncomeCost` after load and after every row save/delete.
 *
 * Inline editing uses a clone-shadow pattern (`prodPartsClone`): on row-edit-init a copy
 * of the row is stored by unique `cloneIndex`; on cancel the original is restored from the
 * clone; on save the API is called (`postProdParts` or `putProdPartsById`) and the table is
 * reloaded.
 *
 * The `proPartUpdate$` Subject handles product-part lookups when the user selects a part
 * via the inline product selector: it fires `getProdInfoById` via `switchMap` and merges the
 * result back into the table row.
 *
 * `onAskEditing` runs a two-step safety check: first checks for any stock-movement documents
 * linked to the product; if only invoices are found it proceeds to check for internal
 * component movements.  Sets `allowEditing` accordingly.
 *
 * Excel export is handled by `onExportData` using the `xlsx` and `file-saver` libraries.
 *
 * **Output:** `prodPartsUpdated` emits the current `ProductParts[]` after any list mutation.
 */
@Component({
  selector: 'app-edit-product-parts',
  templateUrl: './edit-product-parts.component.html',
  styleUrls: ['./edit-product-parts.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditProductPartsComponent implements OnInit, OnDestroy, OnChanges {

  globalSettinsData: GlobalSettingsData;

  /** The product whose component parts are being displayed and edited. */
  @Input() product: Product;

  /** Current list of component rows displayed in the table. */
  prodParts: ProductParts[];
  selectedProdPart: ProductParts;

  /** Shadow clone array used for cancel/undo of in-progress row edits. */
  prodPartsClone: ProductParts[] = [];

  @ViewChild('prodPartsTable') prodPartsTable: Table;

  /** Emits the current `ProductParts[]` after any list mutation. */
  @Output() prodPartsUpdated: EventEmitter<ProductParts[]> = new EventEmitter();

  rowIndex: number;
  editMode: boolean;

  /** `true` when the product is a simple (non-complex) product (`PRODKIND == 0`). */
  public get productIsSimple(): boolean {
    return (this.product) && (this.product.PRODKIND == 0);
  }

  /** `true` when no part row is selected. */
  public get noPpRowSelected(): boolean {
    return this.selectedProdPart ? false : true;
  }

  confirmDeleteOn: boolean = false;

  private _getProdPartQuery: Subscription;
  private _productQuery: Subscription;
  private _productPartsQuery: Subscription;
  private _prodPartsInfoQuery: Subscription;
  private _prodPutQuery: Subscription;

  /** Pushes a `ProductParts` row when the user selects a part product in the inline selector. */
  private proPartUpdate$ = new Subject<ProductParts>();

  allowComplex: boolean = false;
  allowOld: boolean = false;

  /** `true` when the row has a `PARTID` set and can be saved. */
  public allowSave(pp: ProductParts): boolean { return pp && (pp.PARTID ? true : false); }

  cf3: any = {};

  loading: boolean = false;

  /** Running total income cost = Σ(`PARTPRICE × QUANTITY`) across all rows. */
  totalIncomeCost: number = 0;

  /** Set by `onAskEditing`; controls whether the edit toolbar is enabled. */
  allowEditing: boolean = false;

  constructor(
    private prodApi: ProductApiService,
    private router: Router,
    private messageService: MessageService,
    private docApi: DocumentApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private filterService: FilterService,
  ) { }

  /** Unsubscribes all active HTTP subscriptions to prevent memory leaks. */
  ngOnDestroy(): void {
    console.log('*** EditProductPartsComponent in ngOnDestroy')

    if (this._getProdPartQuery) { this._getProdPartQuery.unsubscribe(); }

    if (this._productQuery) { this._productQuery.unsubscribe(); }

    if (this._productPartsQuery) { this._productPartsQuery.unsubscribe(); }

    if (this._prodPartsInfoQuery) { this._prodPartsInfoQuery.unsubscribe(); }

    if (this._prodPutQuery) { this._prodPutQuery.unsubscribe(); }
  }

  /** Recalculates `totalIncomeCost` and emits `prodPartsUpdated` with the current list. */
  countIncomeCost(pp: ProductParts[]) {

    this.totalIncomeCost = 0;

    this.prodParts.forEach(pp => {
      this.totalIncomeCost += (pp.PARTPRICE * pp.QUANTITY);
    });

    console.log('*** EditProductPartsComponent countIncomeCost: ===> emit');

    this.prodPartsUpdated.emit(pp);
  }

  /** Fetches `ProductParts[]` and enriches each row with `getProdInfoById` pricing/stock data. */
  loadProdParts() {

    this.loading = true;
    this._getProdPartQuery = this.prodApi.getProdParts(this.product.PRODID)
      .pipe(
        concatMap(data => {

          console.log('*** EditProductPartsComponent loadProductContent: ===> ProdParts');
          environment.production || console.table(data);

          this.prodParts = data;
          this.product._PRODPARTS = this.prodParts;

          return from(data);
        }),

        tap(pp => pp.cloneIndex = 0),
        concatMap(pp => this.prodApi.getProdInfoById(
          pp.PARTID,
          this.globalSettinsData.mainCompanyId,
          this.globalSettinsData.balanceCurrencyId,
          this.globalSettinsData.mainStockId,
          this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
          this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
          new Date()
        )
          .pipe(
            filter(ppi => ppi.length > 0),
            concatMap(ppi => from(ppi)),

            concatMap(pi => {

              pp.PARTCODE = pi.PRODCODE;
              pp.PARTNAME = pi.PRODNAME;
              pp.PARTSTOCK = pi.PRODSTOCK;
              pp.PARTSOLD = pi.PRODSOLD;
              pp.PARTRESERVE = pi.PRODRESERVE;
              pp.PARTONWAY = pi.PRODONWAY;
              pp.PARTPRICE = pi.PRODINPRICE;

              return of(pp);
            })
        )),

        finalize(() => {
          this.countIncomeCost(this.prodParts);
          this.loading = false;
          console.log('*** EditProductPartsComponent loadProductContent: ===> 7451556430643DDDDDDAAAAAMMMMMMMM ' + this.totalIncomeCost);
        })
      )
      .subscribe();
  }

  /** Reacts to `product` changes by reloading the BOM via `loadProdParts`. */
  ngOnChanges(changes: SimpleChanges) {
    console.log('*** EditProductPartsComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      //environment.production || console.log('*** EditProductPartsComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //environment.production || console.log('*** EditProductPartsComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {
        case 'product':
          this.loadProdParts();
          break;
      }
    }
  }

  /** Registers custom PrimeNG filter strategies, sets up the `proPartUpdate$` pipeline, and loads the BOM. */
  ngOnInit(): void {
    console.log('*** EditProductPartsComponent ngOnInit:');

    this.filterService.register('gtOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value > filter))
    )
    );

    this.filterService.register('GteOrZero', (value: number, filter: number) => (
      ((filter == 0) && (value == 0)) || ((filter > 0) && (value >= filter))
    )
    );

    this._prodPartsInfoQuery =
      this.proPartUpdate$
      .pipe(
        switchMap(prodp => this.prodApi.getProdInfoById(prodp.PARTID,
          this.globalSettinsData.mainCompanyId,
          this.globalSettinsData.balanceCurrencyId,
          this.globalSettinsData.mainStockId,
          this.globalSettinsData.impCountFlag ? this.globalSettinsData.impStockId : 0,
          this.globalSettinsData.auxCountFlag ? this.globalSettinsData.auxStockId : 0,
          new Date()).pipe(

            filter(data => data.length > 0),
            concatMap(data => from(data)),

            concatMap(data => {

              let prodq = this.prodParts.findIndex(e => e.PARTID == prodp.PARTID);

              if (prodq >= 0) {
                prodp = {
                  ...prodp,

                  PARTCODE: data.PRODCODE,
                  PARTNAME: data.PRODNAME,
                  PARTPRICE: data.PRODINPRICE,
                  PARTSTOCK: data.PRODSTOCK,
                  PARTSOLD: data.PRODSOLD,
                  PARTRESERVE: data.PRODRESERVE,
                };

                this.prodParts[prodq] = prodp;
              }

              this.prodParts = [...this.prodParts];

              //environment.production || console.table(prodp);
              //environment.production || console.table(this.prodParts);
              //environment.production || console.table(this.prodPartsClone);

              return of(prodp);
            }),

          )),
      )
       .subscribe();

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.loadProdParts();
  }

  /** Handles table row selection — currently a no-op. */
  onRowSelect(event) {
    //console.log('*** EditProductPartsComponent onRowSelect: ' + JSON.stringify(event.data));
  }

  /** Handles table row deselection — currently a no-op. */
  onRowUnselect(event) {
    //console.log('*** EditProductPartsComponent onRowUnselect: ' + JSON.stringify(event));
  }

  /** Opens the delete-confirmation dialog for the selected part row. */
  onRemoveProdPart(event) {
    console.log('*** EditProductPartsComponent onRemoveProdPart: ' + JSON.stringify(event));

    this.confirmDeleteOn = true;
  }

  /** Handles the delete-confirmation result — calls `deleteProdPartsById` on confirm and reloads the list. */
  onDeleteConfirmation(event) {
    console.log('*** EditProductPartsComponent onDeleteConfirmation: ' + JSON.stringify(event));
    this.confirmDeleteOn = false;

    if (event) {
      console.log('*** EditProductPartsComponent DO DELETE!!! NOW!!!');

      let vr = this.prodParts.findIndex(e => e.PPID == this.selectedProdPart.PPID);

      if (vr != -1) {
/*
 * Call the database DELETE API call here
 */
        console.log('*** EditProductPartsComponent GOING TO DELETE NOW: ' + vr);

        this.prodApi.deleteProdPartsById(this.selectedProdPart.PRODID, this.selectedProdPart.PPID).subscribe((data) => console.log('*** EditProductPartsComponent onDeleteConfirmation: Deleted: ' + data))
        this.prodParts.splice(vr, 1);
        this.prodParts = [...this.prodParts];

        this.countIncomeCost(this.prodParts);

      //  environment.production || console.table(this.prodParts);
      }
   }
  }

  /** Handles product selection from the inline product picker — pushes the row onto `proPartUpdate$` to enrich it. */
  onProductPartSelection(event: Product, pp: ProductParts) {
    console.log('*** EditProductPartsComponent onProductPartSelection: ' + JSON.stringify(event));
    pp.PARTID = event.PRODID;

    this.proPartUpdate$.next(pp);
  }

  /** Appends a new blank part row to `prodParts` and enters it into inline-edit mode. */
  onAddProdPart(event) {
    console.log('*** EditProductPartsComponent onAddProdPart: ' + JSON.stringify(event));

    //environment.production || console.table(this.prodParts);

    let newPP: ProductParts = {
      PPID: -2,
      PRODID: this.product.PRODID,
      PARTID: null /*this.product.PRODID*/,
      PARTCODE: '',
      QUANTITY: 1,
      PARTNOTE: '99. Новый компонент',

      PARTNAME: '',
      PARTSTOCK: 0,
      PARTSOLD: 0,
      PARTRESERVE: 0,
      PARTONWAY: 0,
      PARTPRICE: 0,

      cloneIndex: 0
    };
//    this.prodParts.unshift(newPP);

    this.prodParts = [...this.prodParts, newPP];

    let ppe: ProductParts = this.prodParts.find(e => e.PPID == -2);
    this.onPpRowEditInit(ppe);
    this.prodPartsTable.initRowEdit(ppe);
  }

  /** Creates a clone snapshot of `esg` in `prodPartsClone` and enters edit mode. */
  onPpRowEditInit(esg: ProductParts) {
    console.log('*** EditProductPartsComponent in onPpRowEditInit: entry');
    environment.production || console.table(esg);

    let newCloneIndex: number = 0;

    /*
     * Quite tricky algoritm:
     * Because we can edit simultaneously more than one row in the table.
     * Therefore for every edited row we have to create cloned copy in shadow array [securityGroupsClone]
     * We can't predict the order of starting and ending the edit process
     * 
     * So - Search for max cloneIndex in current table and assign +1 to newly created row copy
     *
     * When row either cancelled or saved - clone index resetted to 0, clone copy deleted from shadow array
     *
     */
    this.prodParts.forEach(pp => { newCloneIndex = Math.max(newCloneIndex, pp.cloneIndex) });

    console.log('*** EditProductPartsComponent in onPpRowEditInit: newCloneIndex is: ' + newCloneIndex);

    esg.cloneIndex = ++newCloneIndex;
    this.prodPartsClone.push({ ...esg });

    this.editMode = true;

  //  console.log('*** EditProductPartsComponent in onPpRowEditInit: clone created');
  //  environment.production || console.table(esg);
  //  console.log('*** EditProductPartsComponent in onPpRowEditInit: clone ');
  //  environment.production || console.table(this.prodPartsClone);
  }

  /** Cancels inline editing — removes new rows or restores existing rows from `prodPartsClone`. */
  onPpRowEditCancel(esg: ProductParts, index: number) {
    console.log('*** EditProductVendorsComponent in onPpRowEditCancel: ' + index);
    environment.production || console.table(esg);

    let ppid = this.prodPartsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    if (esg.PPID == -2) {
      // Cancel the newly created record
      // simply delete

      this.prodParts.splice(index, 1);
    } else {
      // Cancel editing of existing record
      // have to reload it from database

      this.prodParts[index] = { ...this.prodPartsClone[ppid] };
      this.prodParts[index].cloneIndex = 0;
    }

    this.prodPartsClone.splice(ppid, 1);

    this.prodParts = [...this.prodParts];

    this.editMode = false;

  //  console.log('*** EditProductPartsComponent in onPpRowEditSave: prodParts');
  //  environment.production || console.table(this.prodParts);
  //  console.log('*** EditProductPartsComponent in onPpRowEditSave: prodPartsClone ');
  //  environment.production || console.table(this.prodPartsClone);
  }

  /** Saves a row: calls `postProdParts` for new rows or `putProdPartsById` for existing ones, then reloads. */
  onPpRowEditSave(esg: ProductParts) {
    console.log('*** EditProductPartsComponent in onRowEditSave: ');
    environment.production || console.table(esg);

    let ppid = this.prodPartsClone.findIndex(sg => sg.cloneIndex == esg.cloneIndex);

    this._productPartsQuery =
      concat(((esg.PPID == -2) ? this.prodApi.postProdParts(this.product.PRODID, esg) : this.prodApi.putProdPartsById(this.product.PRODID, this.prodPartsClone[ppid].PPID, esg))
        .pipe(map((count) => {
          console.log('*** EditProductPartsComponent in onSubscribe to Save: ' + count);

          this.prodPartsClone.splice(ppid, 1);
          esg.cloneIndex = 0;
          this.editMode = false;
          this.loadProdParts();
        })),

      )
        .subscribe();
  }

  /** Navigates to the edit view of the selected component product. */
  onProdPartLink(event, pp: ProductParts) {
    environment.production || console.log('*** EditProductPartsComponent in onProdPartLink: event' + JSON.stringify(event));
    this.router.navigate(['/edit-product', pp.PARTID]);
  }

  /** Reloads the BOM table via `loadProdParts`. */
  reloadTable(event) {
    console.log('*** EditProductPartsComponent reloadTable: ');

    this.loadProdParts();
  }

  /** Writes `buffer` to an `.xlsx` file using `file-saver`. */
  saveAsExcelFile(buffer: any, fileName: string): void {
    console.log('*** EditProductPartsComponent in saveAsExcelFile: ');
    import("file-saver").then(FileSaver => {
      const EXCEL_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });

      saveAs(data, fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION);
    });
  }

  /** Exports `prodParts` to an Excel file via the `xlsx` library. */
  onExportData() {
    console.log('*** EditProductPartsComponent in onExportData: ');

    import("xlsx").then(xlsx => {

      environment.production || console.table(this.prodParts);

      const exportData: any =
        this.prodParts.map(e => ({

          'Часть': e.PARTNOTE,
          'Код': e.PARTCODE,
          'Описание': e.PARTNAME,
          'Количество': e.QUANTITY,
          'На складе': e.PARTSTOCK,
          'Входная цена': e.PARTPRICE,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { Parts: worksheet }, SheetNames: ['Parts'] };

      const excelBuffer: any = xlsx.write(workbook, { bookType: "xlsx", type: "array" });

      this.saveAsExcelFile(excelBuffer, "Parts");
    });
  }

  // AskEditing
  // ********************************************************************************
  // Very global procedure to check whether product content could be changed
  // Criteria to allow editing:
  // 1. Product is not linked to any documents (stock, sales, purchases, etc)
  // 2. Product is linked to invoices only (then we can allow editing with warning)
  //
  // Criteria to definitely forbid editing:
  // 1. Product is linked to stock movements (then we can't allow editing)
  // 2. Some internal movement of components exists (documents with NOTE2='')

  /**
   * Checks whether the product's BOM can safely be edited.
   *
   * Step 1: calls `findDocumentsByParameters` for stock-movement document types [1–5].
   * - No docs → allow editing.
   * - Stock movements present → forbid (sets `allowEditing = false`).
   * - Invoices only → allow, proceed to step 2.
   *
   * Step 2: checks for internal component movements (`DOCTYPE=5`, `DOCNOTE2` contains `PRODCODE`).
   * - Any found → forbid; none → allow.
   *
   * Shows a PrimeNG toast if editing is forbidden.
   */
  onAskEditing() {

    this.allowEditing = false;
    let doNextCheck: boolean = true;
    let localAllowEditing: boolean = false;

    let message: string = 'Товар связан с движением на складе';

    this._productPartsQuery =
      this.docApi.findDocumentsByParameters([{ mode: 'PRODID', value: this.product.PRODID }, { mode: 'DOCTYPE', value: [1, 2, 3, 4, 5] }]).pipe(

        tap(docs => environment.production || (console.table(docs))),

        map(docs => {
          if (docs.length == 0) {

            // There is no documents linked to product at all
            // Lets do anything we want

            // Honestly - could be exist internal movement of parts with this product as component
            // But without parent documenst it is very less probable

            localAllowEditing = true;
            doNextCheck = false;
          } else {
            // There are some documents linked to product
            // Need to check them

            let thereIsStockMovement: boolean = false;
            docs.forEach(doc => {
              if ([2, 3, 4, 5].includes(doc.DOCTYPE)) {
                thereIsStockMovement = true;
              }
            })

            if (thereIsStockMovement) {
              // There is stock movement - definitely forbid editing
              localAllowEditing = false;
              doNextCheck = false;
              message = 'Товар связан с движением на складе';
              return;
            }
            // No stock movements - could be sales/purchase invoices only

            let onlyInvoices: boolean = true;
            docs.forEach(doc => {
              if (doc.DOCTYPE != 1) {
                onlyInvoices = false;
              }
            });

            if (onlyInvoices) {
              message = 'Товар связан только со счетами, можно редактировать с предупреждением';

              // There are only invoices - allow editing with warning
              localAllowEditing = true;
              doNextCheck = true;
            }
          }
        }),

        // Here we have to check also internal movements of components of this product
        // Internal movements have NOTE2 = contatins PRODCODE of this product

        filter(() => doNextCheck),

        concatMap(() => this.docApi.findDocumentsByParameters([
          { mode: 'DOCTYPE', value: [5] },
          // Here we need to check whether document really contains anything to block the editing.
          // Recent investigation and current message implementation gives us case - empty document with DOCNOTE2 contains product code.
          // There is no reason to have such empty stub, but i've forced to clean the DOCNOTE2 to allow editing.
          // { mode: 'DOCFIELD', value: { fieldName: 'PRIMDOCID', operMode: 'equal', fieldValue:  } },
          { mode: 'DOCFIELD', value: { fieldName: 'DOCNOTE2', operMode: 'contain', fieldValue: this.product.PRODCODE } }
        ]).pipe(

          tap(docs => environment.production || (console.table(docs))),

          map(docs => {
            if (docs.length > 0) {
            // There are some internal movements of components - forbid editing
              localAllowEditing = false;
              doNextCheck = false;
              message = 'Товар связан с внутренними перемещениями компонентов, редактирование запрещено';
            } else {
            // No internal movements of components - allow editing
              localAllowEditing = true;
              doNextCheck = true;
              message = 'Товар не связан с внутренними перемещениями компонентов, редактирование разрешено';
            }
          })
        )),

        finalize(() => {
          // Checking finished - emit event
          this.allowEditing = localAllowEditing;

          console.log('*** EditProductPartsComponent onAskEditing: ===> emit allowEditing=' + this.allowEditing);
          if (!this.allowEditing) {
            this.messageService.add({ severity: 'error', summary: 'Редактирование запрещено', detail: message });
          }
        })

      ).subscribe();

  }
}
